/**
 * Session Energy Utilities
 * 
 * IMPORTANT: OCPP chargers report Energy.Active.Import.Register as a cumulative
 * lifetime meter reading (like an odometer), NOT per-session energy.
 * 
 * Example:
 *   meter_start_kwh = 269.219
 *   latest_meter_kwh = 269.800
 *   meter_stop_kwh = 271.000
 * 
 * Correct session energy calculation:
 *   Live:  latest_meter_kwh - meter_start_kwh = 0.581 kWh
 *   Final: meter_stop_kwh - meter_start_kwh = 1.781 kWh
 * 
 * These utilities ensure "Energy Delivered" always shows per-session energy,
 * never the charger's cumulative lifetime meter total.
 */

/**
 * Get live session energy for active charging sessions
 * Uses delta calculation: current_meter - meter_start
 * 
 * @param {object} session - Session state object
 * @param {object} session.live - Live session data
 * @param {number} session.live.energyKWh - Session energy (already calculated as delta)
 * @param {number} session.live.meter_value - Raw meter value (cumulative - do not use directly)
 * @param {number} session.live.meter_start - Starting meter value
 * @returns {number} Live session energy in kWh (always >= 0)
 */
export function getLiveSessionEnergy(session) {
  if (!session?.live) {
    return 0;
  }

  // PREFERRED: Use pre-calculated energyKWh from SessionContext
  // This is already calculated as (latest_meter - meter_start) in SessionContext
  if (session.live.energyKWh != null) {
    const energy = Number(session.live.energyKWh);
    return isNaN(energy) ? 0 : Math.max(0, energy);
  }

  // FALLBACK: Calculate from raw meter values if needed
  // (This path should rarely be used - SessionContext does this calculation)
  const latestMeter = Number(session.live.meter_value) || 0;
  const meterStart = Number(session.live.meter_start) || 0;
  
  if (latestMeter > 0 && meterStart >= 0) {
    const delta = latestMeter - meterStart;
    return Math.max(0, delta);
  }

  return 0;
}

/**
 * Get final session energy for completed sessions
 * Prefers API-provided summary, falls back to calculated delta
 * 
 * @param {object} session - Session state object
 * @param {object} serverSession - Server session object (optional)
 * @param {number} serverSession.summary_energy_kwh - API-calculated final energy
 * @param {number} serverSession.energy_kwh - Legacy API field
 * @param {object} session.live - Live session data (fallback)
 * @param {number} session.live.energyKWh - Session energy delta
 * @param {number} session.live.meter_stop - Final meter reading
 * @param {number} session.live.meter_start - Starting meter reading
 * @returns {number} Final session energy in kWh (always >= 0)
 */
export function getFinalSessionEnergy(session, serverSession = null) {
  // Collect all available energy sources
  const serverFinal = serverSession?.final_energy_kwh != null
    ? Number(serverSession.final_energy_kwh) : null;
  const summaryRaw = serverSession?.summary?.energy_kwh ?? serverSession?.summary_energy_kwh;
  const serverSummary = summaryRaw != null ? Number(summaryRaw) : null;
  const serverLegacy = serverSession?.energy_kwh != null
    ? Number(serverSession.energy_kwh) : null;
  const liveEnergy = session?.live?.energyKWh != null
    ? Number(session.live.energyKWh) : null;

  // Calculate from meter values as last resort. Prefer meter_stop (captured
  // at StopTransaction) but fall back to the latest live cumulativeMeter
  // when StopTransaction didn't carry a valid meter_stop — this rescues
  // sessions where the OCPP StopTransaction was bridged/synthesised and the
  // only real meter snapshot we have is the last MeterValues frame.
  const meterStart = Number(session?.live?.meter_start);
  const meterStop = Number(session?.live?.meter_stop);
  const cumulativeMeter = Number(session?.live?.cumulativeMeter);

  const meterDelta = (Number.isFinite(meterStop) && meterStop > 0 && Number.isFinite(meterStart) && meterStart >= 0)
    ? Math.max(0, meterStop - meterStart)
    : null;
  const cumulativeDelta = (Number.isFinite(cumulativeMeter) && cumulativeMeter > 0 && Number.isFinite(meterStart) && meterStart >= 0)
    ? Math.max(0, cumulativeMeter - meterStart)
    : null;

  // PRIORITY 1: Server finalized energy (authoritative) — but only if > 0.
  // If server says 0, check if live tracking has a higher value (server may have
  // finalized before StopTransaction arrived with real meter data).
  if (serverFinal != null && !isNaN(serverFinal) && serverFinal > 0) {
    return serverFinal;
  }

  // PRIORITY 2: API summary energy
  if (serverSummary != null && !isNaN(serverSummary) && serverSummary > 0) {
    return serverSummary;
  }

  // PRIORITY 3: Legacy API field
  if (serverLegacy != null && !isNaN(serverLegacy) && serverLegacy > 0) {
    return serverLegacy;
  }

  // PRIORITY 4: Live energyKWh (already calculated as delta by SessionContext)
  if (liveEnergy != null && !isNaN(liveEnergy) && liveEnergy > 0) {
    return liveEnergy;
  }

  // PRIORITY 5: Calculate from meter_stop - meter_start
  if (meterDelta != null && meterDelta > 0) {
    return meterDelta;
  }

  // PRIORITY 6: Latest cumulative meter - meter_start (last-resort when
  // StopTransaction wasn't received but live MeterValues did flow).
  if (cumulativeDelta != null && cumulativeDelta > 0) {
    return cumulativeDelta;
  }

  // Server explicitly set 0 (e.g. free session or genuinely 0 energy)
  if (serverFinal != null && !isNaN(serverFinal) && serverFinal === 0) {
    return 0;
  }

  return 0;
}

/**
 * Format energy value for display
 * 
 * @param {number} energyKWh - Energy in kWh
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted energy string (e.g., "1.78 kWh")
 */
export function formatEnergyKwh(energyKWh, decimals = 2) {
  const energy = Number(energyKWh);
  
  if (isNaN(energy) || energy < 0) {
    return '0.00 kWh';
  }

  return `${energy.toFixed(decimals)} kWh`;
}

/**
 * Check if session is active (can receive live energy updates)
 * 
 * @param {object} session - Session state object
 * @returns {boolean} True if session is actively charging
 */
export function isSessionActive(session) {
  if (!session?.live) {
    return false;
  }

  // Session must not have ended
  if (session.live.endTime) {
    return false;
  }

  // Session must have a transaction ID or start time
  if (!session.live.tx_id && !session.live.startTime) {
    return false;
  }

  // Check status
  const ACTIVE_STATUSES = ['Charging', 'Preparing', 'SuspendedEV', 'SuspendedEVSE'];
  const status = session.live.status;
  
  if (status && !ACTIVE_STATUSES.includes(status)) {
    return false;
  }

  return true;
}

/**
 * Get appropriate energy value based on session state
 * Automatically selects live or final energy
 * 
 * @param {object} session - Session state object
 * @param {object} serverSession - Server session object (optional)
 * @returns {number} Energy in kWh (always >= 0)
 */
export function getSessionEnergy(session, serverSession = null) {
  const isActive = isSessionActive(session);
  
  if (isActive) {
    return getLiveSessionEnergy(session);
  } else {
    return getFinalSessionEnergy(session, serverSession);
  }
}
